import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { api } from '../../utils/api';

/**
 * AI-vbof (Feature B, Gap 1) — the `/reset-password` landing page.
 *
 * The backend `forgotPassword` already emails `${FRONTEND_URL}/reset-password?token=…`,
 * but this page did not exist, so the link 404'd. This page reads the token
 * from the query string, collects a new password + confirmation, and POSTs to
 * `/auth/reset-password`. On success it redirects to `/login`.
 *
 * The reset token is single-use (bound to the user's `passwordHash`); after a
 * successful reset the same token can never verify again.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('No reset token in the link. Please request a new one from the forgot-password page.');
  }, [token]);

  const strengthHint = newPassword.length > 0 && newPassword.length < 8
    ? 'Use at least 8 characters.'
    : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('No reset token in the link. Please request a new one.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired reset link. Please request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="aura-root flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--ivory-0)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-bold"
            style={{
              background: 'linear-gradient(135deg, var(--ink-0), var(--ink-1))',
              color: 'var(--ivory-0)',
              fontFamily: 'var(--ff-serif)',
            }}
          >
            A
          </div>
          <h1
            className="mt-4 text-2xl"
            style={{ fontFamily: 'var(--ff-serif)', fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}
          >
            {done ? 'Password reset' : 'Set a new password'}
          </h1>
        </div>

        <div
          className="rounded-xl p-8"
          style={{ background: 'var(--ivory-1)', border: '1px solid var(--line)', boxShadow: 'var(--sh-2)' }}
        >
          {done ? (
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--ink-1)' }}>
                Your password has been reset. Redirecting to sign in…
              </p>
              <p className="mt-2 text-xs" style={{ color: 'var(--ink-3)' }}>
                <Link to="/login" className="font-medium" style={{ color: 'var(--saffron-600)' }}>
                  Go to sign in
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div
                  className="mb-4 rounded-lg p-3 text-sm"
                  style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
                >
                  {error}
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="newPassword" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                  New password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none"
                    style={{ background: 'var(--ivory-0)', color: 'var(--ink-0)', border: '1px solid var(--line)' }}
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--ink-3)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {strengthHint && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--ink-3)' }}>{strengthHint}</p>
                )}
              </div>
              <div className="mb-6">
                <label htmlFor="confirm" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
                  style={{ background: 'var(--ivory-0)', color: 'var(--ink-0)', border: '1px solid var(--line)' }}
                  placeholder="Re-enter the new password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !token}
                className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)' }}
              >
                {submitting ? <Loader2 size={16} className="mx-auto animate-spin" /> : 'Reset password'}
              </button>
            </form>
          )}

          {!done && (
            <p className="mt-4 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
              <Link to="/login" className="font-medium" style={{ color: 'var(--saffron-600)' }}>
                Back to sign in
              </Link>
            </p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">Powered by Ardhanex Tech Pvt Ltd</p>
      </div>
    </div>
  );
}
